import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    Card,
    Typography,
} from "@mui/material";
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { NewspaperOutlined } from '@mui/icons-material';
import { Parser } from 'html-to-react';

import { useNewsHook } from '../../Hooks/Dashboard/newsHooks';

import { NewsList } from '../../Models/Dashboard/News';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { NewsListField } from '../../Utilities/Enums/ParametersEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import AddEditViewNewsModal from './AddEditViewNewsModal';

const NewsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
`;

const HeaderContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: flex;
    justify-content: space-between;
    box-shadow: none !important;
    align-items: center;
    width: -webkit-fill-available;
    
    .newsAnnouncementText {
        font-size: 1.09375rem;
        font-weight: 600;
    }
`;

const NewsItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 20px;
    box-shadow: none !important;
`;

const NewsItem = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: 1px solid;
    border-radius: 4px !important;
    box-shadow: none !important;
    // display: grid;
    // grid-template-columns: 1fr;
    align-items: center;
    cursor: pointer;

    .postedByContainer {
        display: grid;
        grid-template-columns: 6fr 1fr;
        align-items: center;
        padding: 10px 20px;

        .postedBy {
            font-size: 12px !important;
            font-weight: 400;
            color: #878a99;
            text-align: left;
        }

        .actionButtonsContainer {
            display: flex;
            justify-content: space-between;
            align-items: center;

            button {
                width: 27px;
                height: 27px;
                padding: 5px;
            }
        }
    }

    .coverPhotoContainer {
        width: -webkit-fill-available;
        height: 250px;
        align-content: center;

        img {
            // object-fit: cover;
            // width: 100%;
            height: 100%;
        }

        .newsIcon {
            width: 150px;
            height: 150px;
            color: ${(props) => (props.$darkMode ? '#ffffff1f' : '#0000001f')};
        }
    }

    .detailsContainer {
        padding: 20px;
        text-align: left;

        .newsName {
            font-size: 16px;
            font-weight: 600 !important;
        }

        .newsDescription {
            font-size: 13px;
            font-weight: 400 !important;
            margin: 10px 0px 20px 0px;
            max-height: 59px;

            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .newsDate {
            font-size: 12px !important;
            font-weight: 400;
            color: #878a99;
        }
    }
`;

export default function News() {
    let isDarkMode = LocalStorageHelper.isDarkMode();

    //Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),

        sortBy: QueryBuilderHelper.DefaultSortValue(NewsListField.DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [newsList, setnewsList] = useState<any>([]);
    let [openAddEditViewNewsModal, setopenAddEditViewNewsModal] = useState<boolean>(false);
    let [viewType, setviewType] = useState<ViewType>(ViewType.ADD);
    let [newsPublicId, setnewsPublicId] = useState<string | number | null>('');
    let [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);

    const q_queryKey = [CachingEnum.NEWS, queryStr]
    const { data, fetchStatus, getNewsCoverPhotoById, deleteNewsById } = useNewsHook(q_queryKey, queryStr);

    //Event Handlers
    const initializeData = async (_newsList: NewsList[]) => {
        let nl: NewsList[] = [];

        if(_newsList?.length > 0) {
            for (const [newsIndex, newsItem] of Object.entries(_newsList)) {
                let _imgURL: any = '';

                let firstParagraph: any = new DOMParser().parseFromString(newsItem?.[NewsListField.DESCRIPTION] ?? "", 'text/html');

                if(newsItem?.[NewsListField.HAS_COVER_IMAGE] && newsItem?.[NewsListField.PUBLIC_ID]) {
                    const responseData: any = await getNewsCoverPhotoById(newsItem?.[NewsListField.PUBLIC_ID]);

                    _imgURL = responseData?.data ?? null;
                }

                nl.push({ ...newsItem, [NewsListField.COVER_IMAGE_SRC]: _imgURL, [NewsListField.DESCRIPTION]: firstParagraph?.querySelector('p')?.textContent ?? "" });
            } 
        }

        setnewsList(nl);
    }

    useEffect(() => {
        if(fetchStatus != "fetching") {
            initializeData(data?.data?.list ?? []);
        }
    }, [fetchStatus, data]);

    //Rendering Functions
    const renderHeaders = () => {
        return <HeaderContainer>
            <Typography color="primaryText" className="newsAnnouncementText">News / Announcements</Typography>
            {AccessibilityHelper.hasAccess(AdminModules.NEWS_CREATE) && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Add News'
                startIcon={<IconPlus />}
                onClick={() => {
                    setopenAddEditViewNewsModal(true);
                    setviewType(ViewType.ADD);
                }} />}
        </HeaderContainer>
    }

    const renderNews = () => {
        return <NewsItemsContainer>
            {newsList?.map((news: NewsList, newsIndex: number) => (<>
                <NewsItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}} onClick={(event: any) =>{
                    event.preventDefault();
                    
                    setopenAddEditViewNewsModal(true);
                    setviewType(ViewType.PREVIEW);
                    setnewsPublicId(news?.[NewsListField.PUBLIC_ID] ?? "");
                }}>
                    <div className="postedByContainer">
                        <Typography className="postedBy">Posted by {news?.[NewsListField.CREATED_BY_NAME] ?? "--"}</Typography>
                        <div className="actionButtonsContainer">
                            {AccessibilityHelper.hasAccess(AdminModules.NEWS_EDIT) &&<CustomIconButton toolTipName='Edit'
                                icon={<IconEdit color='var(--textInfoColor)' />}
                                onClick={(e: any) => {
                                    e.preventDefault();

                                    setopenAddEditViewNewsModal(true);
                                    setviewType(ViewType.EDIT);
                                    setnewsPublicId(news?.[NewsListField.PUBLIC_ID] ?? "");
                                }} />}

                            {AccessibilityHelper.hasAccess(AdminModules.NEWS_DELETE) &&<CustomIconButton toolTipName='Delete'
                                icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                                onClick={(e: any) => {
                                    setopenDelete(true);
                                    settoDeleteItem(news);
                                }} />}
                        </div>
                    </div>

                    <div className="coverPhotoContainer">
                        {news?.[NewsListField.COVER_IMAGE_SRC] && <img className="" src={news?.[NewsListField.COVER_IMAGE_SRC]} />}
                        {!news?.[NewsListField.COVER_IMAGE_SRC] && <NewspaperOutlined className="newsIcon" />}
                        
                    </div>

                    <div className="detailsContainer">
                        <Typography className="newsName">{news?.[NewsListField.NAME] ?? "--"}</Typography>
                        <Typography className="newsDescription">{Parser().parse(news?.[NewsListField.DESCRIPTION] ?? "--")}</Typography>
                        {news?.[NewsListField.DATE] && <Typography className="newsDate">{DateHelper.getFormattedDate(news?.[NewsListField.DATE], "dd Month, yyyy")}</Typography>}
                    </div>
                </NewsItem>
            </>))}
        </NewsItemsContainer>
    }

    return <NewsContainer>
        {renderHeaders()}
        {renderNews()}

        {openAddEditViewNewsModal && <AddEditViewNewsModal
            openAddEditViewNewsModal={openAddEditViewNewsModal}
            setopenAddEditViewNewsModal={setopenAddEditViewNewsModal}
            viewType={viewType}
            setviewType={setviewType}
            newsPublicId={newsPublicId}
            setnewsPublicId={setnewsPublicId} />}

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={toDeleteItem?.[NewsListField.NAME] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[NewsListField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteNewsById(toDeleteItem?.[NewsListField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                    }
                }
            }} /> }
    </NewsContainer>
}
